import {
  useState,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  type ReactNode,
  type CSSProperties,
} from 'react'
import {
  AnimatePresence,
  MotionConfig,
  motion,
  useInstantLayoutTransition,
  useReducedMotion,
  type Variants,
} from 'framer-motion'
import { GlobalState, useGlobalEvent } from '@highlight/ipc'
import { useOpenModals } from '@renderer/chat'
import { useAtom } from 'jotai'
import {
  useSidepanelHotkeyOpenedAtom,
  useSetSidepanelOpenedViaKeybind,
  useSidepanelCustomWidth,
  useSetSidepanelGeometry,
  useSidepanelSide,
  useQuickTourActive,
  clampSidepanelWidth,
  DEFAULT_SIDEPANEL_WIDTH,
  DEFAULT_SIDEPANEL_LEFT_PADDING,
} from '../../atoms'
import { useViewNavigation } from '../../view-atoms'
import { oppositeSide, sidepanelSideSign } from '@shared/sidepanel/side'
import { sidepanelOpenFocusIntentAtom, useSidepanel, useSetSidepanelPeekSource } from '@renderer/overlay/atoms'
import { Interactive, setOverlayInteractive } from '@highlight/ui'
import OverlayIpcRenderer from '@renderer/ipc/OverlayIpc.renderer'

import { SidepanelPeekTrigger } from './nested/peek-trigger'
import { SidepanelContent } from './nested/content'
import { SidepanelToastProvider, SidepanelToasts } from './nested/toasts'
import { SidepanelFooter } from './nested/footer'
import { SidepanelSection } from './nested/section'
import { SidepanelSectionLabel } from './nested/section-label'
import { SidepanelSectionHeader, SidepanelSectionHeaderLead } from './nested/section-header'
import { SidepanelSectionTitleGroup } from './nested/section-title-group'
import { SidepanelSectionIcon } from './nested/section-icon'
import { SidepanelSectionSubtitle } from './nested/section-subtitle'
import { SidepanelSectionViewAll } from './nested/section-view-all'
import { SidepanelSectionDragHandle } from './nested/section-drag-handle'
import { SidepanelHeader } from './nested/header'
import { SidepanelBody } from './nested/body'
import { SidepanelTrigger } from './nested/trigger'
import { Announcement } from '@renderer/components/announcement'
import { SIDEPANEL_CLOSE_TWEEN, SIDEPANEL_OPEN_TWEEN, SIDEPANEL_SPRING } from '../../constants'
import {
  trackSidepanelOpened,
  trackSidepanelClosed,
  trackSidepanelResized,
  type SidepanelResizeHandle,
} from '@renderer/overlay/utils/track-sidepanel'
import { SidepanelSectionBody } from './nested/section-body'
import { CornerResizeHandle } from './nested/corner-resize-handle'
import { ResizeHandle } from './nested/resize-handle'
import { VerticalResizeHandle } from './nested/vertical-resize-handle'
import { SidepanelDragDropZones } from './nested/drag-drop-zones'
import { useSidepanelDrag, sidepanelDragControlsRef } from '../../hooks/use-sidepanel-drag'
import { SidepanelTourDragStrip } from './nested/tour-drag-strip'
import { useSidepanelFocusIntent } from '../../hooks/use-sidepanel-focus-intent'
import { useSidepanelGeometryPublisher } from '../../hooks/use-sidepanel-geometry-publisher'
import { useSidepanelMotionOverride } from '../../hooks/use-sidepanel-motion-override'
import { useSidepanelPeek } from '../../hooks/use-sidepanel-peek'
import { useSidepanelVerticalLayout } from '../../hooks/use-sidepanel-vertical-layout'
import { SIDEPANEL_MIN_HEIGHT, defaultSidepanelHeight } from '../../sidepanel-vertical-layout'
import { useViewportSnapshot } from '@renderer/overlay/hooks/use-viewport-snapshot'
import { useStagedSidepanelMotion } from '../../hooks/use-staged-sidepanel-motion'

interface SidepanelProps {
  children: ReactNode
}

/** The panel, with its own toasts: anything below raises them, and `Sidepanel.Toasts` is where they show. */
function Sidepanel({ children }: SidepanelProps) {
  return (
    <SidepanelToastProvider>
      <SidepanelFrame>{children}</SidepanelFrame>
    </SidepanelToastProvider>
  )
}

function SidepanelFrame({ children }: SidepanelProps) {
  const [sidepanelState, setSidepanelState] = useSidepanel()
  const reducedMotion = useReducedMotion() ?? false
  const motionState = useStagedSidepanelMotion(sidepanelState, reducedMotion)
  const setPeekSource = useSetSidepanelPeekSource()
  const [, setHotkeyOpened] = useSidepanelHotkeyOpenedAtom()
  const setOpenedViaKeybind = useSetSidepanelOpenedViaKeybind()
  const [focusIntent, setFocusIntent] = useAtom(sidepanelOpenFocusIntentAtom)
  const { topView, canGoBack } = useViewNavigation()
  const sidepanelIsVisible = sidepanelState === 'open' || sidepanelState === 'peeking'

  // The panel's own `<Interactive>` (below) covers the panel band, which is all
  // the panel itself needs. Modals and onboarding are genuinely full-screen and
  // aren't wrapped per-element, so they need a column-wide surface that outlives
  // the band — registered only while one of those is up, and deferred off so it
  // survives a brief close animation.
  //
  // Read quick-tour from its Jotai atom (the source of truth in this renderer)
  // rather than the GlobalState mirror published in a later effect — the
  // mirror lags by a render, which would leave the panel click-through for a
  // frame right as a full-screen overlay appears.
  const hasOpenModal = useOpenModals().length > 0
  const quickTourActive = useQuickTourActive()
  const needsFullHeight = hasOpenModal || quickTourActive
  const [expandToFull, setExpandToFull] = useState(needsFullHeight)
  useEffect(() => {
    if (needsFullHeight) {
      setExpandToFull(true)
      return
    }
    const timeout = setTimeout(() => setExpandToFull(false), 300)
    return () => clearTimeout(timeout)
  }, [needsFullHeight])

  const customWidth = useSidepanelCustomWidth()
  const setGeometry = useSetSidepanelGeometry()

  const side = useSidepanelSide()
  // +1 anchored right, -1 anchored left — multiplies every x offset that
  // points "toward the panel's screen edge" (hide/swipe/exit directions).
  const sideSign = sidepanelSideSign(side)

  // Tracked so a display change (drag across monitors, resolution switch)
  // re-clamps the width and re-emits bounds.
  const viewport = useViewportSnapshot()

  const sidepanelWidth = clampSidepanelWidth(customWidth)

  // Height and vertical position within the available band (viewport minus the
  // menu bar and a bottom dock the panel's column overlaps). Lands on the root below, so every
  // descendant that is `h-full` or positioned against the root follows for free.
  const verticalLayout = useSidepanelVerticalLayout()

  // Drag-to-reposition (header drag surface starts it via sidepanelDragControlsRef)
  const { isDragging, isDocking, isRepositioning, dragY, hoveredSide, pointerX, pointerY, startDrag } =
    useSidepanelDrag(verticalLayout)
  useLayoutEffect(() => {
    sidepanelDragControlsRef.current = { start: startDrag }
    return () => {
      sidepanelDragControlsRef.current = null
    }
  }, [startDrag])

  const isExitingRef = useRef(false) // Tracks if exit animation is in progress

  // Temporary x-override on the panel root: the two-finger swipe-to-close
  // gesture and the re-dock entrance after a side change (one owner, since
  // both drive the same motion value).
  const {
    x: overrideX,
    isOverrideActive,
    resetForClose,
    clearOverride,
  } = useSidepanelMotionOverride({ side, sidepanelWidth })

  const sidepanelVariants: Variants = {
    hidden: { x: sidepanelWidth * sideSign },
    peeking: { x: sidepanelWidth * sideSign },
    // Open and exit carry their own tween: these are the two the native blur
    // animates alongside, and it can only match a curve it can express. The
    // root `transition` below stays SIDEPANEL_SPRING for hidden/peeking.
    open: { x: 0, transition: SIDEPANEL_OPEN_TWEEN },
    exit: { x: sidepanelWidth * sideSign, transition: SIDEPANEL_CLOSE_TWEEN },
  }

  // Every reopen path can land mid-exit, and each one has to finish the cleanup
  // handleExitComplete will now never run. One owner, so a new open path can't
  // reopen with half the state still reading "closing".
  const finishExitCleanup = useCallback(() => {
    if (!isExitingRef.current) return
    isExitingRef.current = false
    clearOverride()
    setHotkeyOpened(false)
    setOpenedViaKeybind(false)
    setFocusIntent(null)
  }, [clearOverride, setHotkeyOpened, setOpenedViaKeybind, setFocusIntent])

  const { onPanelMouseEnter, cancelPendingHide } = useSidepanelPeek({
    sidepanelState,
    setSidepanelState,
    setPeekSource,
    finishExitCleanup,
  })

  // Sync the open bit used by main's hotkey check without fanning unchanged
  // hidden ↔ peeking transitions back out to every renderer.
  useEffect(() => {
    void GlobalState.setIfChangedInMain('sidepanel.isOpen', sidepanelState === 'open').catch((error) => {
      console.error('[Sidepanel] Failed to sync open state', error)
    })
  }, [sidepanelState])

  // Track previous state to detect actual close transitions
  const prevStateForExitRef = useRef(sidepanelState)

  // Track when exit animation starts (only from open → hidden, not peeking → hidden)
  useEffect(() => {
    const wasOpen = prevStateForExitRef.current === 'open'
    const isNowHidden = !sidepanelIsVisible
    if (wasOpen && isNowHidden) {
      isExitingRef.current = true
    }
    // Clear exiting flag if sidepanel reopened during exit animation
    if (sidepanelIsVisible && isExitingRef.current) {
      isExitingRef.current = false
    }
    prevStateForExitRef.current = sidepanelState
  }, [sidepanelState, sidepanelIsVisible])

  const { focusRemindersInput } = useSidepanelFocusIntent({
    sidepanelState,
    focusIntent,
    setFocusIntent,
    canGoBack,
    topViewId: topView.id,
  })

  // Handle hotkey to open sidepanel
  const onSidepanelHotkeyPressed = useCallback(() => {
    // The flags this clears are re-set below, so the interrupted-exit cleanup
    // and this open settle on the same values in one batch.
    finishExitCleanup()
    cancelPendingHide()
    trackSidepanelOpened('hotkey')
    setHotkeyOpened(true)
    setOpenedViaKeybind(true)
    setSidepanelState('open')
  }, [setOpenedViaKeybind, setHotkeyOpened, setSidepanelState, finishExitCleanup, cancelPendingHide])
  useGlobalEvent('sidepanelHotkeyPressed', onSidepanelHotkeyPressed)

  // Not gated on the quick tour: step 1 teaches this hotkey, so pressing it has
  // to close the panel. The tour is only hidden along with the panel, and
  // reopening resumes on the same step.
  const onSidepanelHotkeyClose = useCallback(() => {
    resetForClose()
    setOverlayInteractive(false)
    trackSidepanelClosed('hotkey')
    setSidepanelState('hidden')
  }, [setSidepanelState, resetForClose])
  useGlobalEvent('sidepanelHotkeyClose', onSidepanelHotkeyClose)

  // Handle hotkey to open sidepanel and focus reminders input
  const onRemindersHotkeyPressed = useCallback(
    (overlayWasFocused: boolean) => {
      // Only close if overlay already had OS focus and input was focused
      if (overlayWasFocused && document.activeElement?.matches('[data-reminders-input]')) {
        setOverlayInteractive(false)
        setSidepanelState('hidden')
        return
      }

      // If sidepanel is already open, focus immediately
      if (sidepanelState === 'open') {
        focusRemindersInput()
        return
      }

      // Clears focusIntent too, but this sets its own immediately after.
      finishExitCleanup()
      setFocusIntent('reminders')
      setSidepanelState('open')
    },
    [sidepanelState, setFocusIntent, setSidepanelState, focusRemindersInput, finishExitCleanup],
  )
  useGlobalEvent('remindersHotkeyPressed', onRemindersHotkeyPressed)

  const { onMotionUpdate, onMotionComplete } = useSidepanelGeometryPublisher({
    isOpen: sidepanelState === 'open' && motionState === 'open',
    side,
    sidepanelWidth,
    viewportWidth: viewport.width,
    verticalLayout,
    dragY,
    isRepositioning,
  })

  const dynamicSidepanelStyle = useMemo(
    () => ({
      background: 'transparent',
      '--sidepanel-left-padding': `${DEFAULT_SIDEPANEL_LEFT_PADDING}px`,
    }),
    [],
  ) as CSSProperties

  // The reorderable sections use framer `layout`, which would otherwise animate
  // their position delta when the panel width commits. Wrapping the width write
  // in an instant layout transition makes framer apply the new layout in one
  // frame (no slide) while leaving reorder drag animations untouched.
  const startInstantLayout = useInstantLayoutTransition()

  // One reporter for all three handles. Each commits only part of the geometry,
  // so the dimension a handle doesn't touch is reported at its current value.
  const trackResize = useCallback(
    (handle: SidepanelResizeHandle, newWidth: number, newHeight: number) => {
      trackSidepanelResized({
        handle,
        width: newWidth,
        previousWidth: sidepanelWidth,
        height: newHeight,
        previousHeight: verticalLayout.height,
        defaultWidth: DEFAULT_SIDEPANEL_WIDTH,
        defaultHeight: defaultSidepanelHeight(verticalLayout.bandHeight),
        bandHeight: verticalLayout.bandHeight,
        viewportWidth: viewport.width,
        viewportHeight: viewport.height,
      })
    },
    [sidepanelWidth, verticalLayout.height, verticalLayout.bandHeight, viewport.width, viewport.height],
  )

  const handleResizeComplete = useCallback(
    (newWidth: number) => {
      trackResize('width', newWidth, verticalLayout.height)
      startInstantLayout(() => setGeometry({ width: newWidth }))
    },
    [startInstantLayout, setGeometry, trackResize, verticalLayout.height],
  )

  // Height and offset commit together: dragging the top edge changes both (the
  // bottom edge has to stay put), so writing them separately would render one
  // frame with the new height still at the old offset.
  const handleVerticalResizeComplete = useCallback(
    (newHeight: number, newOffset: number) => {
      trackResize('height', sidepanelWidth, newHeight)
      startInstantLayout(() => setGeometry({ height: newHeight, verticalOffset: newOffset }))
    },
    [startInstantLayout, setGeometry, trackResize, sidepanelWidth],
  )

  // Corner drag changes width, height, and offset in one gesture — commit them
  // together so framer layout doesn't animate a staggered intermediate frame.
  const handleCornerResizeComplete = useCallback(
    (newWidth: number, newHeight: number, newOffset: number) => {
      trackResize('corner', newWidth, newHeight)
      startInstantLayout(() => setGeometry({ width: newWidth, height: newHeight, verticalOffset: newOffset }))
    },
    [startInstantLayout, setGeometry, trackResize],
  )

  const handleExitComplete = useCallback(() => {
    // Only run full cleanup when exiting from open state (not peek dismissal)
    if (!isExitingRef.current) return
    finishExitCleanup()
    OverlayIpcRenderer.closeSidepanel()
  }, [finishExitCleanup])

  return (
    // One declaration for every framer-motion animation in the panel — the view
    // swap, the nav chevrons, the notepad, the floating bar, the Day Zero
    // banner. `"user"` drops transform and layout animation while keeping
    // opacity, so motion-sensitive users get cuts instead of slides and every
    // surface still arrives. Drag is untouched, so the swipe gesture and the
    // resize handle still follow the pointer.
    <MotionConfig reducedMotion="user">
      <SidepanelPeekTrigger />
      {/* Column-wide interactive surface, only for full-screen modals and
          onboarding (which aren't wrapped per-element). Home cards, the chat
          input, and the trigger/lock buttons register their own tight bounds;
          drill-in Views ride the panel band's region below (and force a frame
          on mount — see view-router.tsx), so nothing else needs one.
          Deliberately a sibling of the panel root rather than a child: the root
          is only as tall as the panel now, and these surfaces are positioned
          against the screen — they can sit above or below a short panel. */}
      {sidepanelState === 'open' && expandToFull && (
        <div className="pointer-events-none fixed top-0 h-full" style={{ [side]: 0, width: sidepanelWidth }}>
          <Interactive />
        </div>
      )}
      {/* Rendered outside the panel's transformed root so fixed positioning
          resolves against the screen, not the panel's transform. */}
      {isDragging && (
        <SidepanelDragDropZones
          hoveredSide={hoveredSide}
          sidepanelWidth={sidepanelWidth}
          bandTop={verticalLayout.bandTop}
          pointerX={pointerX}
          pointerY={pointerY}
          // A ghost tracking the cursor beside a panel that already does is noise.
          showGhost={isDocking || !isRepositioning}
        />
      )}
      <AnimatePresence onExitComplete={handleExitComplete}>
        {sidepanelIsVisible && (
          <motion.div
            key="sidepanel"
            data-sidepanel-root
            data-sidepanel-side={side}
            className="pointer-events-none fixed isolate z-30 flex flex-col"
            style={{
              ...dynamicSidepanelStyle,
              [side]: 0,
              top: verticalLayout.top,
              height: verticalLayout.height,
              width: sidepanelWidth,
              ...(isOverrideActive && { x: overrideX }),
              // Only while a reposition drag is live: an always-present `y`
              // would leave a permanent transform on the root.
              ...(isRepositioning && { y: dragY }),
            }}
            variants={sidepanelVariants}
            initial="hidden"
            animate={motionState}
            exit="exit"
            transition={SIDEPANEL_SPRING}
            onMouseEnter={onPanelMouseEnter}
            onUpdate={onMotionUpdate}
            onAnimationComplete={onMotionComplete}
          >
            {/* Keep the panel band interactive across gaps between its surfaces.
                The screen-edge gutter stays click-through, while half the inner
                gutter remains interactive to make the width-resize strip easier
                to grab. Individual controls still register their own bounds. */}
            <div className="pointer-events-none absolute inset-y-0" style={{ [side]: 12, [oppositeSide(side)]: 6 }}>
              <Interactive />
            </div>
            <div data-sidepanel-content className="pointer-events-none flex h-full w-full flex-col">
              {/* While merely peeking, the panel sits fully off-screen and only the
                  chevron (SidepanelPeekTrigger, rendered outside this tree) is
                  visible — so don't mount the heavy Home/View tree. Hover
                  peeks (edge proximity, Mini logo) used to mount and unmount the
                  entire panel per hover, which burned real CPU/energy in the
                  overlay renderer. The exit render is preserved by AnimatePresence
                  from the last open render, so close animations still show content. */}
              {sidepanelState === 'open' && children}
            </div>
            {/* The tour's drag strip sits under every handle below, so the
                resize edges keep their hit targets while it is up. */}
            {sidepanelState === 'open' && quickTourActive && <SidepanelTourDragStrip />}
            {/* Edge handles first; free-corner handles last so they own the
                diagonal hit targets (same z-index, later in paint order). */}
            {sidepanelState === 'open' && verticalLayout.bandHeight > SIDEPANEL_MIN_HEIGHT && (
              <>
                <VerticalResizeHandle
                  edge="top"
                  layout={verticalLayout}
                  side={side}
                  onResizeComplete={handleVerticalResizeComplete}
                />
                <VerticalResizeHandle
                  edge="bottom"
                  layout={verticalLayout}
                  side={side}
                  onResizeComplete={handleVerticalResizeComplete}
                />
              </>
            )}
            {sidepanelState === 'open' && (
              <ResizeHandle
                sidepanelWidth={sidepanelWidth}
                defaultWidth={DEFAULT_SIDEPANEL_WIDTH}
                clampWidth={clampSidepanelWidth}
                onResizeComplete={handleResizeComplete}
                side={side}
              />
            )}
            {sidepanelState === 'open' && verticalLayout.bandHeight > SIDEPANEL_MIN_HEIGHT && (
              <>
                <CornerResizeHandle
                  corner="top"
                  layout={verticalLayout}
                  side={side}
                  sidepanelWidth={sidepanelWidth}
                  defaultWidth={DEFAULT_SIDEPANEL_WIDTH}
                  clampWidth={clampSidepanelWidth}
                  onResizeComplete={handleCornerResizeComplete}
                />
                <CornerResizeHandle
                  corner="bottom"
                  layout={verticalLayout}
                  side={side}
                  sidepanelWidth={sidepanelWidth}
                  defaultWidth={DEFAULT_SIDEPANEL_WIDTH}
                  clampWidth={clampSidepanelWidth}
                  onResizeComplete={handleCornerResizeComplete}
                />
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </MotionConfig>
  )
}

// Nested components
Sidepanel.Content = SidepanelContent
Sidepanel.Trigger = SidepanelTrigger
Sidepanel.Header = SidepanelHeader
Sidepanel.Announcement = Announcement
Sidepanel.Body = SidepanelBody
Sidepanel.Footer = SidepanelFooter
Sidepanel.Toasts = SidepanelToasts
Sidepanel.Section = SidepanelSection
Sidepanel.SectionHeader = SidepanelSectionHeader
Sidepanel.SectionHeaderLead = SidepanelSectionHeaderLead
Sidepanel.SectionTitleGroup = SidepanelSectionTitleGroup
Sidepanel.SectionIcon = SidepanelSectionIcon
Sidepanel.SectionLabel = SidepanelSectionLabel
Sidepanel.SectionSubtitle = SidepanelSectionSubtitle
Sidepanel.SectionViewAll = SidepanelSectionViewAll
Sidepanel.SectionDragHandle = SidepanelSectionDragHandle
Sidepanel.SectionBody = SidepanelSectionBody

export { Sidepanel }
